import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { comparisonsDetail, ComparisonDetail } from '../../../mocks/comparisons-detail';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';

function ScoreBar({ score, gradient }: { score: number; gradient: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${gradient}`}
          style={{ width: `${score * 10}%` }}
        ></div>
      </div>
      <span className="text-xs font-black text-gray-800 w-8 text-right flex-shrink-0">{score.toFixed(1)}</span>
    </div>
  );
}

export default function ComparisonDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';
  const data: ComparisonDetail | undefined = comparisonsDetail.find((c) => c.slug === slug);

  useEffect(() => {
    if (!data) navigate('/comparisons');
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data, navigate]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!data) return null;

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
  const winnerName = data.overallWinner === 'A' ? data.toolA.name : data.overallWinner === 'B' ? data.toolB.name : null;
  const winnerGradient = data.overallWinner === 'A' ? data.toolA.bgGradient : data.toolB.bgGradient;

  const avgA = (data.scores.reduce((s, r) => s + r.scoreA, 0) / data.scores.length).toFixed(1);
  const avgB = (data.scores.reduce((s, r) => s + r.scoreB, 0) / data.scores.length).toFixed(1);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${siteUrl}/comparisons/${data.slug}#article`,
        headline: `${data.toolA.name} vs ${data.toolB.name}: Which is Better in 2026?`,
        description: `In-depth comparison of ${data.toolA.name} vs ${data.toolB.name}. Side-by-side analysis of features, pricing, and performance to help you choose the right tool.`,
        url: `${siteUrl}/comparisons/${data.slug}`,
        dateModified: data.updatedDate,
        author: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
        publisher: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
        articleSection: data.category,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Comparisons', item: `${siteUrl}/comparisons` },
          { '@type': 'ListItem', position: 3, name: `${data.toolA.name} vs ${data.toolB.name}`, item: `${siteUrl}/comparisons/${data.slug}` },
        ],
      },
      {
        '@type': 'ItemList',
        name: `${data.toolA.name} vs ${data.toolB.name} Score Comparison`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: data.toolA.name,
            item: { '@type': 'SoftwareApplication', name: data.toolA.name, aggregateRating: { '@type': 'AggregateRating', ratingValue: avgA, bestRating: 10 } },
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: data.toolB.name,
            item: { '@type': 'SoftwareApplication', name: data.toolB.name, aggregateRating: { '@type': 'AggregateRating', ratingValue: avgB, bestRating: 10 } },
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title={`${data.toolA.name} vs ${data.toolB.name} (2026) – Full Comparison | 247digitrend`}
        description={`${data.toolA.name} vs ${data.toolB.name}: Side-by-side comparison of features, pricing, ease of use, and performance. ${winnerName ? `Our pick: ${winnerName}.` : 'See which tool is right for you.'}`}
        canonical={`${siteUrl}/comparisons/${data.slug}`}
        keywords={`${data.toolA.name} vs ${data.toolB.name}, ${data.category} comparison, best ${data.category} tools 2026`}
        ogType="article"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Home</Link>
            <Link to="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Categories</Link>
            <Link to="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Reviews</Link>
            <Link to="/comparisons" className="text-[19px] font-semibold text-violet-600">Comparisons</Link>
            <Link to="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Blog</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/comparisons" className="text-sm font-medium text-gray-600 hover:text-violet-600 transition-colors flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-arrow-left-line text-base"></i> All Comparisons
            </Link>
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-36 pb-10" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 50%, #fff0fb 100%)' }}>
        <div className="max-w-6xl mx-auto px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-violet-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/comparisons" className="hover:text-violet-600 transition-colors">Comparisons</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600 font-medium">{data.toolA.name} vs {data.toolB.name}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="flex-1">
              <span className="inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full mb-4 bg-violet-50 text-violet-600 border border-violet-100">
                {data.category}
              </span>
              {/* VS display */}
              <div className="flex items-center gap-5 mb-5">
                <div className="flex flex-col items-center gap-2">
                  <div className={`w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${data.toolA.bgGradient} text-white text-2xl`}>
                    <i className={data.toolA.icon}></i>
                  </div>
                  <span className="text-lg font-black text-gray-900">{data.toolA.name}</span>
                </div>
                <div className="flex flex-col items-center gap-1 px-2">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100">
                    <span className="text-sm font-black text-gray-400">VS</span>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className={`w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${data.toolB.bgGradient} text-white text-2xl`}>
                    <i className={data.toolB.icon}></i>
                  </div>
                  <span className="text-lg font-black text-gray-900">{data.toolB.name}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {(data.views / 1000).toFixed(1)}K views</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> Updated {data.updatedDate}</span>
                <span className="flex items-center gap-1"><i className="ri-shield-check-line text-violet-400"></i> Independently reviewed</span>
              </div>
            </div>

            {/* Winner card */}
            <div className="md:w-64 flex-shrink-0">
              <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                {data.overallWinner === 'tie' ? (
                  <>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 text-center">Overall Verdict</p>
                    <div className="flex justify-center gap-2 mb-3">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br ${data.toolA.bgGradient} text-white text-sm`}>
                        <i className={data.toolA.icon}></i>
                      </div>
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
                        <span className="text-xs font-black text-gray-400">=</span>
                      </div>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br ${data.toolB.bgGradient} text-white text-sm`}>
                        <i className={data.toolB.icon}></i>
                      </div>
                    </div>
                    <p className="text-sm font-black text-gray-900 text-center mb-1">It\'s a Tie</p>
                    <p className="text-xs text-gray-500 text-center leading-relaxed">Both tools excel in different areas</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 text-center">Our Pick</p>
                    <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${winnerGradient} text-white text-2xl mx-auto mb-3`}>
                      <i className={data.overallWinner === 'A' ? data.toolA.icon : data.toolB.icon}></i>
                    </div>
                    <p className="text-base font-black text-gray-900 text-center mb-1">{winnerName} Wins</p>
                    <div className="flex justify-center mb-3">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600">
                        <i className="ri-award-fill text-xs"></i> Editor\'s Choice
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* LEFT: Main content */}
            <div className="flex-1 min-w-0 space-y-10">

              {/* Intro */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-file-text-line text-violet-500"></i> Overview
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed">{data.intro}</p>
              </div>

              {/* Scores Table */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-violet-500"></i> Head-to-Head Scores
                </h2>
                <div className="rounded-2xl border border-gray-100 overflow-hidden">
                  {/* Header row */}
                  <div className="grid grid-cols-[1fr_180px_180px] gap-0 bg-gray-50 border-b border-gray-100 px-5 py-3">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Criteria</span>
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider text-center">{data.toolA.name}</span>
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider text-center">{data.toolB.name}</span>
                  </div>
                  {data.scores.map((row, i) => {
                    const aWins = row.scoreA > row.scoreB;
                    const bWins = row.scoreB > row.scoreA;
                    return (
                      <div key={i} className={`grid grid-cols-[1fr_180px_180px] gap-0 px-5 py-4 border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                        <div className="flex flex-col justify-center">
                          <span className="text-sm font-semibold text-gray-800">{row.label}</span>
                          <span className="text-xs text-gray-400 mt-0.5">{row.note}</span>
                        </div>
                        <div className="flex flex-col justify-center px-4">
                          <div className="flex items-center gap-1 mb-1">
                            {aWins && <i className="ri-arrow-up-circle-fill text-emerald-500 text-xs flex-shrink-0"></i>}
                          </div>
                          <ScoreBar score={row.scoreA} gradient={data.toolA.bgGradient} />
                        </div>
                        <div className="flex flex-col justify-center px-4">
                          <div className="flex items-center gap-1 mb-1">
                            {bWins && <i className="ri-arrow-up-circle-fill text-emerald-500 text-xs flex-shrink-0"></i>}
                          </div>
                          <ScoreBar score={row.scoreB} gradient={data.toolB.bgGradient} />
                        </div>
                      </div>
                    );
                  })}
                  {/* Totals */}
                  <div className="grid grid-cols-[1fr_180px_180px] gap-0 px-5 py-4 bg-violet-50">
                    <span className="text-sm font-black text-gray-900 flex items-center">Average Score</span>
                    <div className="px-4 flex items-center justify-center">
                      <span className={`text-lg font-black bg-gradient-to-r ${data.toolA.bgGradient} text-transparent bg-clip-text`}>
                        {avgA}
                      </span>
                    </div>
                    <div className="px-4 flex items-center justify-center">
                      <span className={`text-lg font-black bg-gradient-to-r ${data.toolB.bgGradient} text-transparent bg-clip-text`}>
                        {avgB}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Deep Dives */}
              <div className="space-y-8">
                <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
                  <i className="ri-microscope-line text-violet-500"></i> In-Depth Analysis
                </h2>
                {data.deepDives.map((dive, i) => (
                  <div key={i} className="rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex-shrink-0">
                        <i className={`${dive.icon} text-white text-xs`}></i>
                      </div>
                      <h3 className="text-sm font-black text-gray-900">{dive.title}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br ${data.toolA.bgGradient} flex-shrink-0`}>
                            <i className={`${data.toolA.icon} text-white text-xs`}></i>
                          </div>
                          <span className="text-xs font-bold text-gray-700">{data.toolA.name}</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{dive.contentA}</p>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br ${data.toolB.bgGradient} flex-shrink-0`}>
                            <i className={`${data.toolB.icon} text-white text-xs`}></i>
                          </div>
                          <span className="text-xs font-bold text-gray-700">{data.toolB.name}</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{dive.contentB}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pros & Cons */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-scales-3-line text-violet-500"></i> Pros &amp; Cons
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Tool A */}
                  <div className="space-y-3">
                    <div className={`flex items-center gap-2 p-3 rounded-xl bg-gradient-to-r ${data.toolA.bgGradient} text-white`}>
                      <i className={`${data.toolA.icon} text-base`}></i>
                      <span className="text-sm font-black">{data.toolA.name}</span>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                      <p className="text-xs font-bold text-emerald-700 mb-3 flex items-center gap-1.5"><i className="ri-thumb-up-fill"></i> Pros</p>
                      <ul className="space-y-2">
                        {data.prosA.map((p, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-emerald-500 text-xs mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs text-gray-700 leading-tight">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                      <p className="text-xs font-bold text-red-600 mb-3 flex items-center gap-1.5"><i className="ri-thumb-down-fill"></i> Cons</p>
                      <ul className="space-y-2">
                        {data.consA.map((c, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-400 text-xs mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs text-gray-700 leading-tight">{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {/* Tool B */}
                  <div className="space-y-3">
                    <div className={`flex items-center gap-2 p-3 rounded-xl bg-gradient-to-r ${data.toolB.bgGradient} text-white`}>
                      <i className={`${data.toolB.icon} text-base`}></i>
                      <span className="text-sm font-black">{data.toolB.name}</span>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                      <p className="text-xs font-bold text-emerald-700 mb-3 flex items-center gap-1.5"><i className="ri-thumb-up-fill"></i> Pros</p>
                      <ul className="space-y-2">
                        {data.prosB.map((p, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-emerald-500 text-xs mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs text-gray-700 leading-tight">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                      <p className="text-xs font-bold text-red-600 mb-3 flex items-center gap-1.5"><i className="ri-thumb-down-fill"></i> Cons</p>
                      <ul className="space-y-2">
                        {data.consB.map((c, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-400 text-xs mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs text-gray-700 leading-tight">{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-violet-500"></i> Pricing Comparison
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tool A Pricing */}
                  <div>
                    <div className={`flex items-center gap-2 mb-4 p-3 rounded-xl bg-gradient-to-r ${data.toolA.bgGradient} text-white`}>
                      <i className={`${data.toolA.icon} text-base`}></i>
                      <span className="text-sm font-black">{data.toolA.name} Plans</span>
                    </div>
                    <div className="space-y-3">
                      {data.pricingA.map((plan, i) => (
                        <div key={i} className={`rounded-xl p-4 border ${plan.highlight ? 'border-violet-200 bg-violet-50' : 'border-gray-100 bg-white'}`}>
                          {plan.highlight && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white px-2 py-0.5 rounded-full mb-2">
                              <i className="ri-fire-line text-xs"></i> Popular
                            </span>
                          )}
                          <div className="flex items-end gap-1 mb-2">
                            <span className="text-xl font-black text-gray-900">{plan.price}</span>
                            <span className="text-xs text-gray-400 mb-0.5">{plan.period}</span>
                            <span className="ml-auto text-xs font-bold text-gray-700">{plan.plan}</span>
                          </div>
                          <ul className="space-y-1.5">
                            {plan.features.map((f, fi) => (
                              <li key={fi} className="flex items-center gap-1.5 text-xs text-gray-600">
                                <i className="ri-check-line text-emerald-500 flex-shrink-0"></i> {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Tool B Pricing */}
                  <div>
                    <div className={`flex items-center gap-2 mb-4 p-3 rounded-xl bg-gradient-to-r ${data.toolB.bgGradient} text-white`}>
                      <i className={`${data.toolB.icon} text-base`}></i>
                      <span className="text-sm font-black">{data.toolB.name} Plans</span>
                    </div>
                    <div className="space-y-3">
                      {data.pricingB.map((plan, i) => (
                        <div key={i} className={`rounded-xl p-4 border ${plan.highlight ? 'border-violet-200 bg-violet-50' : 'border-gray-100 bg-white'}`}>
                          {plan.highlight && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white px-2 py-0.5 rounded-full mb-2">
                              <i className="ri-fire-line text-xs"></i> Popular
                            </span>
                          )}
                          <div className="flex items-end gap-1 mb-2">
                            <span className="text-xl font-black text-gray-900">{plan.price}</span>
                            <span className="text-xs text-gray-400 mb-0.5">{plan.period}</span>
                            <span className="ml-auto text-xs font-bold text-gray-700">{plan.plan}</span>
                          </div>
                          <ul className="space-y-1.5">
                            {plan.features.map((f, fi) => (
                              <li key={fi} className="flex items-center gap-1.5 text-xs text-gray-600">
                                <i className="ri-check-line text-emerald-500 flex-shrink-0"></i> {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Who Should Choose */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-user-heart-line text-violet-500"></i> Who Should Choose What?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="rounded-2xl p-5 border border-gray-100 bg-white">
                    <div className={`flex items-center gap-2 mb-4`}>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br ${data.toolA.bgGradient} text-white`}>
                        <i className={`${data.toolA.icon} text-sm`}></i>
                      </div>
                      <span className="text-sm font-black text-gray-900">Choose {data.toolA.name} if...</span>
                    </div>
                    <ul className="space-y-2.5">
                      {data.chooseA.map((reason, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-arrow-right-circle-fill text-violet-400 text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-xs text-gray-700 leading-relaxed">{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl p-5 border border-gray-100 bg-white">
                    <div className={`flex items-center gap-2 mb-4`}>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br ${data.toolB.bgGradient} text-white`}>
                        <i className={`${data.toolB.icon} text-sm`}></i>
                      </div>
                      <span className="text-sm font-black text-gray-900">Choose {data.toolB.name} if...</span>
                    </div>
                    <ul className="space-y-2.5">
                      {data.chooseB.map((reason, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-arrow-right-circle-fill text-fuchsia-400 text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-xs text-gray-700 leading-relaxed">{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Final Verdict */}
              <div className="rounded-2xl p-7 border border-violet-200" style={{ background: 'linear-gradient(135deg, #faf5ff, #fdf2ff)' }}>
                <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-award-fill text-violet-500"></i> Final Verdict
                </h2>
                <p className="text-sm text-gray-700 leading-relaxed">{data.verdict}</p>
                {winnerName && (
                  <div className={`mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${winnerGradient} text-white text-sm font-bold`}>
                    <i className="ri-trophy-fill"></i> Our Pick: {winnerName}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Sticky sidebar */}
            <div className="lg:w-72 flex-shrink-0 space-y-5">
              <div className="sticky top-36 space-y-5">

                {/* Quick scores */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-4">Quick Scores</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-lg bg-gradient-to-br ${data.toolA.bgGradient} flex-shrink-0`}>
                          <i className={`${data.toolA.icon} text-white text-xs`}></i>
                        </div>
                        <span className="text-xs font-bold text-gray-700 flex-1">{data.toolA.name}</span>
                        <span className={`text-sm font-black bg-gradient-to-r ${data.toolA.bgGradient} text-transparent bg-clip-text`}>
                          {avgA}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full bg-gradient-to-r ${data.toolA.bgGradient}`}
                          style={{ width: `${(data.scores.reduce((s, r) => s + r.scoreA, 0) / data.scores.length) * 10}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-5 h-5 flex items-center justify-center rounded-lg bg-gradient-to-br ${data.toolB.bgGradient} flex-shrink-0`}>
                          <i className={`${data.toolB.icon} text-white text-xs`}></i>
                        </div>
                        <span className="text-xs font-bold text-gray-700 flex-1">{data.toolB.name}</span>
                        <span className={`text-sm font-black bg-gradient-to-r ${data.toolB.bgGradient} text-transparent bg-clip-text`}>
                          {avgB}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full bg-gradient-to-r ${data.toolB.bgGradient}`}
                          style={{ width: `${(data.scores.reduce((s, r) => s + r.scoreB, 0) / data.scores.length) * 10}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Visit links */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-4">Visit Official Sites</h3>
                  <div className="space-y-2.5">
                    <a href={data.toolA.website} target="_blank" rel="noopener noreferrer"
                      className={`flex items-center justify-between p-3 rounded-xl bg-gradient-to-r ${data.toolA.bgGradient} text-white text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity`}>
                      <span>{data.toolA.name}</span>
                      <i className="ri-external-link-line"></i>
                    </a>
                    <a href={data.toolB.website} target="_blank" rel="noopener noreferrer"
                      className={`flex items-center justify-between p-3 rounded-xl bg-gradient-to-r ${data.toolB.bgGradient} text-white text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity`}>
                      <span>{data.toolB.name}</span>
                      <i className="ri-external-link-line"></i>
                    </a>
                  </div>
                </div>

                {/* Related comparisons */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-3">More Comparisons</h3>
                  <div className="space-y-2">
                    {comparisonsDetail
                      .filter((c) => c.slug !== data.slug)
                      .slice(0, 4)
                      .map((c, i) => (
                        <Link key={i} to={`/comparisons/${c.slug}`}
                          className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer group">
                          <i className="ri-scales-3-line text-violet-400 flex-shrink-0"></i>
                          <span className="group-hover:text-violet-600 transition-colors leading-tight">
                            {c.toolA.name} vs {c.toolB.name}
                          </span>
                        </Link>
                      ))}
                    <Link to="/comparisons" className="flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors mt-1 cursor-pointer">
                      View all <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                </div>

                {/* Explore */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-3">Explore More</h3>
                  <div className="space-y-2">
                    <Link to="/reviews" className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer">
                      <i className="ri-star-line text-violet-400"></i> All Reviews
                    </Link>
                    <Link to="/categories" className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer">
                      <i className="ri-grid-line text-violet-400"></i> Browse Categories
                    </Link>
                    <Link to="/blog" className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer">
                      <i className="ri-article-line text-violet-400"></i> Blog
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: '#f9f5ff' }} className="pt-14 pb-6 mt-10">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-10 pb-10 border-b border-gray-200">
            <div className="md:col-span-4">
              <div className="flex items-center mb-4">
                <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                247digitrend provides unbiased reviews and comparisons of digital tools, helping businesses make smarter decisions.
              </p>
            </div>
            <div className="md:col-span-2 md:col-start-6">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Resources</h5>
              {[{ label: 'All Reviews', href: '/reviews' }, { label: 'Comparisons', href: '/comparisons' }, { label: 'Categories', href: '/categories' }, { label: 'Blog', href: '/blog' }].map((item, i) => (
                <Link key={i} to={item.href} className="block text-sm text-gray-500 hover:text-violet-600 transition-colors mb-3">{item.label}</Link>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Company</h5>
              {[{ label: 'About Us', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Submit a Tool', href: '/submit-tool' }, { label: 'Advertise', href: '/advertise' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-violet-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Follow Us</h5>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-instagram-line'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-violet-600 hover:border-violet-300 transition-all cursor-pointer">
                    <i className={`${icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">© 2026 247digitrend. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
